import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";

import TitleBlock from "../../../components/block/TitleBlock";
import TopAppBarHome from "../../../components/top-app-bar/TopAppBarHome";
import ConfirmModal from "../../../components/modal/ConfirmModal";
import SvgLoadingAnimation from "../../../components/loading";
import UserListRow from "../../../components/user-item/UserListRow";
import {
  followsApi,
  useGetFollowersQuery,
  useGetFollowsQuery,
  useBlockUserMutation,
  useFollowUserMutation,
  useUnfollowUserMutation,
  useUnblockUserMutation,
} from "../../../app/services/follows";
import { getUserDisplayName } from "../../../utils/user/displayName";

// ===== 定数 =====

const PAGE_LIMIT = 20;
const SEARCH_DEBOUNCE_MS = 300;

// ビュー識別子。TopAppBarHome の `value` と useUserListPagination の `mode` を共通化する。
const VIEW_FOLLOWERS = "followers";
const VIEW_FOLLOWING = "following";
// TopAppBarHome 内の検索アイコン押下時に渡される識別子（タブとは独立した状態）。
const VIEW_SEARCH = "search";

// ===== Styled Components =====

const ListContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Sentinel = styled.div`
  width: 100%;
  height: 1px;
`;

const EmptyState = styled.div`
  width: 100%;
  text-align: center;
  padding: 32px 0;
  color: var(--soremo-light-gray);
`;

// ===== Hooks =====

const useDebouncedValue = (value, delay) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);

  return debounced;
};

/**
 * 検索 + 無限スクロール対応のユーザーリストフック。
 * `mode` で「フォロワー」「フォロー中」のクエリを切替える。
 *
 * - タブ切替・検索語変更でローカル累積をリセットし、`Follows` / `Followers`
 *   キャッシュも無効化することで、戻ってきたときにリロード相当の最新状態に同期する。
 * - フォロー中タブを開いている間の Bookmark OFF/ON は `followsApi` 側で
 *   invalidatesTags を外しているためリスト位置を保ったまま操作できる。
 */
const useUserListPagination = ({ mode, q }) => {
  const dispatch = useDispatch();
  const [offset, setOffset] = useState(0);
  const [accumulated, setAccumulated] = useState([]);

  const queryArg = useMemo(
    () => ({ q: q || undefined, limit: PAGE_LIMIT, offset }),
    [q, offset],
  );

  const followersResult = useGetFollowersQuery(queryArg, {
    skip: mode !== VIEW_FOLLOWERS,
  });
  const followingResult = useGetFollowsQuery(queryArg, {
    skip: mode !== VIEW_FOLLOWING,
  });
  const result = mode === VIEW_FOLLOWERS ? followersResult : followingResult;

  // 初回マウント時は invalidate を抑止し、無駄な再 fetch を避ける。
  const isFirstSyncRef = useRef(true);
  useEffect(() => {
    setOffset(0);
    setAccumulated([]);
    if (!isFirstSyncRef.current) {
      dispatch(followsApi.util.invalidateTags(["Follows", "Followers"]));
    }
    isFirstSyncRef.current = false;
  }, [mode, q, dispatch]);

  // ページ取得結果を累積（offset=0 のときは置き換え、それ以外は追記）。
  useEffect(() => {
    const data = result.data?.data;
    if (!data) return;
    setAccumulated((prev) => {
      if (data.offset === 0) return data.users;
      const seen = new Set(prev.map((u) => u.id));
      return [
        ...prev,
        ...data.users.filter((u) => !seen.has(u.id)),
      ];
    });
  }, [result.data]);

  const data = result.data?.data;
  const hasNext = !!data?.has_next;
  const isFetching = result.isFetching;

  const loadMore = useCallback(() => {
    if (!hasNext || isFetching) return;
    setOffset((prev) => prev + PAGE_LIMIT);
  }, [hasNext, isFetching]);

  const updateUser = useCallback((userId, patch) => {
    setAccumulated((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, ...patch } : u)),
    );
  }, []);

  return {
    users: accumulated,
    isLoading: result.isLoading,
    isFetching,
    hasNext,
    loadMore,
    updateUser,
  };
};

// ===== Helpers =====

const getRightAction = (activeView, user) => {
  if (activeView === VIEW_FOLLOWING) return "follow-toggle";
  return user.is_blocked ? "delete-blocked" : "delete";
};

const getEmptyMessage = (activeView, hasSearchQuery) => {
  if (hasSearchQuery) return "該当するユーザーが見つかりません";
  return activeView === VIEW_FOLLOWERS
    ? "フォロワーはまだいません"
    : "フォロー中のユーザーはまだいません";
};

// ===== Component =====

const FollowersAndFollowing = () => {
  // 「フォロワー」「フォロー中」のうち現在表示中の側。検索モード中も保持する。
  const [activeView, setActiveView] = useState(VIEW_FOLLOWING);
  // TopAppBarHome に渡す現在 value（タブ切替・検索モード "search" を含む）。
  const [viewMode, setViewMode] = useState(VIEW_FOLLOWING);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);

  const segmentOptions = useMemo(
    () => [
      { label: "フォロワー", value: VIEW_FOLLOWERS },
      { label: "フォロー中", value: VIEW_FOLLOWING },
    ],
    [],
  );

  const handleViewModeChange = useCallback((next) => {
    if (next === VIEW_SEARCH) {
      // 検索アイコン押下。activeView は維持し viewMode のみ "search" に。
      setViewMode(VIEW_SEARCH);
    } else {
      setActiveView(next);
      setViewMode(next);
    }
  }, []);

  const {
    users,
    isLoading,
    isFetching,
    hasNext,
    loadMore,
    updateUser,
  } = useUserListPagination({ mode: activeView, q: debouncedSearch });

  // 削除確認モーダルの対象ユーザー（フォロワータブの「削除」アイコン押下時にセット）
  const [removeTarget, setRemoveTarget] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const [blockUser] = useBlockUserMutation();
  const [followUser] = useFollowUserMutation();
  const [unfollowUser] = useUnfollowUserMutation();
  const [unblockUser] = useUnblockUserMutation();

  // 無限スクロール
  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  // ===== ハンドラ =====

  const handleClickDelete = useCallback((user) => {
    setRemoveTarget(user);
  }, []);

  const handleCancelRemove = useCallback(() => {
    if (isRemoving) return;
    setRemoveTarget(null);
  }, [isRemoving]);

  const handleConfirmRemove = useCallback(async () => {
    if (!removeTarget) return;
    setIsRemoving(true);
    try {
      await blockUser(removeTarget.id).unwrap();
      // ブロック後もフォロワー一覧には残るためリストから除去せず、
      // 「削除中」表示に切り替えるだけ。自分→相手のフォローも併せて解除されるので
      // is_following も false に同期する。
      updateUser(removeTarget.id, { is_blocked: true, is_following: false });
      setRemoveTarget(null);
    } catch (err) {
      console.error("フォロワー削除に失敗:", err);
    } finally {
      setIsRemoving(false);
    }
  }, [removeTarget, blockUser, updateUser]);

  const handleToggleFollow = useCallback(
    async (user) => {
      const next = !user.is_following;
      updateUser(user.id, { is_following: next });
      try {
        if (next) {
          await followUser(user.id).unwrap();
        } else {
          await unfollowUser(user.id).unwrap();
        }
      } catch (err) {
        console.error("フォロー操作に失敗:", err);
        updateUser(user.id, { is_following: !next });
      }
    },
    [followUser, unfollowUser, updateUser],
  );

  const handleClickUnblock = useCallback(
    async (user) => {
      updateUser(user.id, { is_blocked: false });
      try {
        await unblockUser(user.id).unwrap();
      } catch (err) {
        console.error("ブロック解除に失敗:", err);
        updateUser(user.id, { is_blocked: true });
      }
    },
    [unblockUser, updateUser],
  );

  const showEmpty = !isLoading && !isFetching && users.length === 0;
  const removeTargetName = getUserDisplayName(removeTarget) || "このユーザー";

  return (
    <>
      <TitleBlock
        title="フォロワー・フォロー中"
        description="同じプロジェクトに参加したユーザーとは、自動でフォローし合います。フォローはいつでも解除できます。"
      />

      <TopAppBarHome
        options={segmentOptions}
        defaultValue={VIEW_FOLLOWING}
        value={viewMode}
        onChange={handleViewModeChange}
        showSearchInput
        onSearchChange={setSearchInput}
        searchPlaceholder="名前で検索..."
      />

      <div className="u-w100 u-pt16">
        <ListContainer>
          {users.map((user) => (
            <UserListRow
              key={user.id}
              user={user}
              rightAction={getRightAction(activeView, user)}
              isFollowing={user.is_following}
              onClickDelete={handleClickDelete}
              onToggleFollow={handleToggleFollow}
              onClickUnblock={handleClickUnblock}
            />
          ))}

          {showEmpty && (
            <EmptyState className="bodytext-13">
              {getEmptyMessage(activeView, !!debouncedSearch)}
            </EmptyState>
          )}

          {(isLoading || isFetching) && (
            <div className="u-w100 u-row-center u-pt16">
              <SvgLoadingAnimation />
            </div>
          )}

          {hasNext && <Sentinel ref={sentinelRef} />}
        </ListContainer>
      </div>

      {removeTarget && (
        <ConfirmModal
          message={`フォロワーを削除しますか？\n削除されたことは、${removeTargetName}には通知されません。`}
          confirmLabel="削除"
          loading={isRemoving}
          onConfirm={handleConfirmRemove}
          onCancel={handleCancelRemove}
        />
      )}
    </>
  );
};

export default FollowersAndFollowing;
